import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useGetMyNotificationScope,
  useGetSelectableNotificationFacilities,
  useUpdateMyNotificationScope,
} from 'src/api/notification-preference'
import { FacilityTypes } from 'src/enums'
import { INotificationScopeItem, ISelectableFacility } from 'src/generated-types/notification'

const facilityKey = (type: FacilityTypes, id: number) => `${type}:${id}`

interface CustomerScopeSectionProps {
  customerId: EntityId
  customerName?: string
  showName: boolean
}

/**
 * Per-customer locality & group scope. Notifications are additive across customers,
 * so each customer the user belongs to gets its own selector. Empty = all localities
 * the user has access to in that customer.
 */
export const CustomerScopeSection: React.FC<CustomerScopeSectionProps> = ({ customerId, customerName, showName }) => {
  const { t } = useTranslation('common')
  const { data: scopeData } = useGetMyNotificationScope(customerId)
  const { data: facilities } = useGetSelectableNotificationFacilities(customerId)
  const { mutateAsync: saveScope, isLoading: saving } = useUpdateMyNotificationScope(customerId)
  const [scope, setScope] = useState<INotificationScopeItem[]>([])

  useEffect(() => {
    if (scopeData) setScope(scopeData)
  }, [scopeData])

  const options = useMemo(() => facilities ?? [], [facilities])
  const selectedFacilities = useMemo(() => {
    const selectedKeys = new Set(scope.map(s => facilityKey(s.type, s.mappingId)))
    return options.filter(o => selectedKeys.has(facilityKey(o.type, o.id)))
  }, [options, scope])

  const onScopeChange = async (selection: ISelectableFacility[]) => {
    const next = selection.map(f => ({ type: f.type, mappingId: f.id }))
    setScope(next)
    await saveScope({ scope: next })
  }

  return (
    <Box mb={1.5}>
      {showName && (
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          {customerName}
        </Typography>
      )}
      <Autocomplete
        multiple
        size="small"
        disabled={saving || options.length === 0}
        options={options}
        value={selectedFacilities}
        onChange={(_, value) => onScopeChange(value)}
        groupBy={option =>
          option.type === FacilityTypes.localityGroup ? t('notify-locality-groups') : t('notify-locality-single')
        }
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={facilityKey(option.type, option.id)}
              size="small"
              label={option.name}
              color={option.type === FacilityTypes.localityGroup ? 'primary' : 'default'}
              variant="outlined"
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selectedFacilities.length === 0 ? t('notify-localities-all') : undefined}
          />
        )}
      />
    </Box>
  )
}

export default CustomerScopeSection
